import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  MapPin,
  User,
  Star,
  Loader2,
  Calendar,
  Phone,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import tokenService from "../../services/token-service";
import apiUrls from "../../config/api-urls";

const MyJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  const liveStatuses = new Set([
    "pending_offers",
    "searching",
    "handyman_selected",
    "assigned",
    "on_the_way",
    "arrived",
    "work_started",
    "payment_received",
    "in_progress",
  ]);

  const token = tokenService.getToken();
  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filter]);

  useEffect(() => {
    if (filter === "offers") {
      fetchMyOffers();
    } else {
      fetchMyJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.page]);

  const fetchMyJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiUrls.handymanJob.getMyJobs}?tab=${filter}&page=${pagination.page}&limit=${pagination.limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setJobs(result.data || []);
        setPagination(result.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      } else {
        throw new Error(result.message || "Failed to fetch jobs");
      }
    } catch (error) {
      setError(error.message || "Failed to load jobs. Please try again.");
      setJobs([]);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOffers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiUrls.handymanOffers.getMyOffers}?page=${pagination.page}&limit=${pagination.limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch offers: ${response.statusText}`);
      }

      const result = await response.json();

      const offersArr = result?.data?.offers || [];
      const pg = result?.data?.pagination || { page: 1, limit: 10, total: 0, pages: 0 };

      setOffers(offersArr);
      setPagination(pg);
      setJobs([]);
    } catch (error) {
      setError(error.message || "Failed to load offers. Please try again.");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
      case "in_progress":
      case "work_started":
      case "approved":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
      case "assigned":
      case "on_the_way":
      case "arrived":
      case "handyman_selected":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "pending_offers":
      case "searching":
      case "scheduled":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
      case "payment_received":
        return "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400";
      case "cancelled":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "in_progress":
        return "In Progress";
      case "work_started":
        return "Working";
      case "assigned":
        return "Assigned";
      case "on_the_way":
        return "On The Way";
      case "arrived":
        return "Arrived";
      case "approved":
        return "Approved";
      case "payment_received":
        return "Payment Received";
      case "pending_offers":
        return "Pending Offers";
      case "searching":
        return "Searching";
      case "scheduled":
        return "Scheduled";
      case "handyman_selected":
        return "Selected";
      case "cancelled":
        return "Cancelled";
      default:
        return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  // Backend already filters by tab, so no need for client-side filtering
  const filteredJobs = jobs;
  const filteredOffers = offers;

  const handleJobClick = (job) => {
    if (!job?._id) return;
    if (liveStatuses.has(job.status)) {
      navigate("/handyman/tasks", { state: { activeJob: job } });
      return;
    }
    if (job.status === "scheduled") {
      navigate("/handyman/schedule");
      return;
    }
    navigate(`/handyman/my-jobs`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#EFA938]" />
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Loading your jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4 shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Jobs</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track all your completed and ongoing jobs
            </p>
          </div>
          <div className="flex justify-start sm:justify-end">
            <div className="inline-flex gap-2 overflow-x-auto bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-2">
              {[
                { key: "all", label: "All Jobs" },
                { key: "offers", label: "My Offers" },
                { key: "active", label: "Active" },
                { key: "completed", label: "Completed" },
                { key: "cancelled", label: "Cancelled" },
                { key: "disputed", label: "Disputed" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    filter === tab.key
                      ? "bg-[#EFA938] text-white"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Jobs List */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <header className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center gap-3">
          <Briefcase className="h-5 w-5 text-[#EFA938]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Job History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filter === "offers"
                ? `${filteredOffers.length} offer${filteredOffers.length !== 1 ? "s" : ""}`
                : `${filteredJobs.length} job${filteredJobs.length !== 1 ? "s" : ""}`}
            </p>
          </div>
        </header>

        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {filter === "offers" ? (
            filteredOffers.length > 0 ? (
              filteredOffers.map((offer) => {
                const req = offer.serviceRequestId;
                const customer = req?.customerId;
                const mainServiceName = req?.mainServiceId?.name || "Service";
                const subServicesText = Array.isArray(req?.subServiceIds)
                  ? req.subServiceIds.map((s) => s?.name).filter(Boolean).join(", ")
                  : "";
                const serviceType = req?.serviceType;
                const address = req?.location?.address || req?.location?.city || "Location not specified";
                const createdAt = req?.createdAt || offer.createdAt;
                return (
                  <article
                    key={offer._id}
                    className="px-6 py-5 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {mainServiceName}
                              </h3>
                              {serviceType && (
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                                  serviceType === "live"
                                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                    : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                                }`}>
                                  {serviceType === "live" ? "Live" : "Scheduled"}
                                </span>
                              )}
                            </div>
                            {subServicesText && (
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                {subServicesText}
                              </p>
                            )}
                            {req?.description && (
                              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {req.description}
                              </p>
                            )}
                          </div>
                          {offer.status && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                              {getStatusLabel(offer.status)}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-[#EFA938]" />
                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                              {customer?.fullName || "Customer"}
                            </span>
                          </div>
                          {customer?.phoneNumber && (
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                              <Phone className="h-4 w-4 text-[#EFA938]" />
                              {customer.phoneNumber}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#EFA938]" />
                            {address}
                          </div>
                          {createdAt && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-[#EFA938]" />
                              {new Date(createdAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="lg:text-right space-y-2">
                        {(req?.basePrice != null || req?.finalPrice != null) && (
                          <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg">
                            <div>
                              <p className="text-xs text-green-600 dark:text-green-400 font-medium">Price</p>
                              <p className="text-lg font-bold text-green-700 dark:text-green-300">
                                PKR {(req?.finalPrice ?? req?.basePrice ?? 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="px-6 py-12 text-center">
                <Briefcase className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 font-medium">No offers found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Offers matching your filter will appear here
                </p>
              </div>
            )
          ) : filteredJobs.length > 0 ? (
            filteredJobs.map((job) => {
              const completedWork = job?.completedWork;
              const completedWorkDescription = completedWork?.description;
              const completedWorkPictures = Array.isArray(completedWork?.pictures) ? completedWork.pictures : [];
              const completedWorkVideo = completedWork?.video;
              const dispute = job?.dispute;
              const disputeRaisedByRole = dispute?.raisedByRole;
              const disputeReason = dispute?.reason;
              const disputeDescription = dispute?.description;
              const disputeStatus = dispute?.status;

              return (
                <article
                  key={job._id}
                  onClick={() => handleJobClick(job)}
                  className="px-6 py-5 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors cursor-pointer"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Left Section - Job Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {job.mainService || job.jobTitle}
                            </h3>
                            {job.serviceType && (
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                                job.serviceType === "live"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                  : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                              }`}>
                                {job.serviceType === "live" ? "Live" : "Scheduled"}
                              </span>
                            )}
                          </div>
                          {(job.mainService || (job.subServices && job.subServices.length > 0) || job.description) && (
                            <div className="mt-1 space-y-1">
                              {(job.mainService || (job.subServices && job.subServices.length > 0)) && (
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  {job.subServices && job.subServices.length > 0
                                    ? `${job.subServices.join(", ")}`
                                    : ""}
                                </p>
                              )}
                              {job.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {job.description}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            job.status
                          )}`}
                        >
                          {getStatusLabel(job.status)}
                        </span>
                      </div>

                    {/* Customer Info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-[#EFA938]" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                          {job.customerName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="h-4 w-4 text-[#EFA938]" />
                        {job.customerPhone}
                      </div>
                    </div>

                    {/* Location & Time */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#EFA938]" />
                        {job.address}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-[#EFA938]" />
                        {new Date(job.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>

                    {/* Customer Review (if completed) */}
                    {job.status === "completed" && job.customerReview && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-center gap-2 mb-1">
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                            Customer Rating: {job.customerReview.rating}/5
                          </span>
                        </div>
                        {job.customerReview.comment && (
                          <p className="text-sm text-amber-800 dark:text-amber-200">
                            "{job.customerReview.comment}"
                          </p>
                        )}
                      </div>
                    )}

                    {job.status === "disputed" && (disputeRaisedByRole || disputeReason || disputeDescription) && (
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <p className="text-sm font-semibold text-red-900 dark:text-red-200">Dispute</p>

                        {disputeStatus && (
                          <p className="mt-1 text-sm text-red-800 dark:text-red-200">
                            Status: {disputeStatus}
                          </p>
                        )}

                        {disputeRaisedByRole && (
                          <p className="mt-1 text-sm text-red-800 dark:text-red-200">
                            Raised by: {disputeRaisedByRole}
                          </p>
                        )}

                        {disputeReason && (
                          <p className="mt-1 text-sm text-red-800 dark:text-red-200">
                            Reason: {disputeReason}
                          </p>
                        )}

                        {disputeDescription && (
                          <p className="mt-1 text-sm text-red-800 dark:text-red-200">
                            Description: {disputeDescription}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Completed Work (if completed) */}
                    {(job.status === "completed" || job.status === "disputed") && (completedWorkDescription || completedWorkPictures.length > 0 || completedWorkVideo) && (
                      <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3 border border-green-200 dark:border-green-900/50">
                        <p className="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">
                          Completed Work
                        </p>

                        {completedWorkDescription && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Description</p>
                            <p className="text-sm text-gray-800 dark:text-gray-200">{completedWorkDescription}</p>
                          </div>
                        )}

                        {completedWorkPictures.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Pictures</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {completedWorkPictures.map((url, idx) => (
                                <a
                                  key={`${job._id}-completedWorkPicture-${idx}`}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="block"
                                >
                                  <img
                                    src={url}
                                    alt={`Completed work ${idx + 1}`}
                                    className="h-20 w-full rounded-md object-cover border border-gray-200 dark:border-slate-700"
                                    loading="lazy"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {completedWorkVideo && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Video</p>
                            <video
                              src={completedWorkVideo}
                              controls
                              className="w-full max-h-56 rounded-md border border-gray-200 dark:border-slate-700 bg-black"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {job.status === "cancelled" && (job.cancellationReason?.cancelledBy === "customer" || job.cancellationReason?.cancelledBy === "handyman") && (
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-red-800 dark:text-red-200">
                            Cancelled by {job.cancellationReason?.cancelledBy}
                          </span>
                          {job.cancellationReason.cancelledAt && (
                            <span className="text-xs text-red-700 dark:text-red-300">
                              {new Date(job.cancellationReason.cancelledAt).toLocaleString()}
                            </span>
                          )}
                        </div>

                        {job.cancellationReason.reason && (
                          <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                            Reason: {job.cancellationReason.reason}
                          </p>
                        )}

                        {job.cancellationReason.penaltyApplied && (
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            Penalty: PKR {Number(job.cancellationReason.penaltyAmount || 0).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right Section - Earnings */}
                  <div className="lg:text-right space-y-2">
                    <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg">
                      <div>
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                          Earnings
                        </p>
                        <p className="text-lg font-bold text-green-700 dark:text-green-300">
                          PKR {job.earnings.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Base Price: PKR {job.basePrice.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">
                        Final Price: PKR {job.finalPrice.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
              );
            })
          ) : (
            <div className="px-6 py-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No jobs found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Jobs matching your filter will appear here
              </p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing page <span className="font-semibold text-gray-900 dark:text-white">{pagination.page}</span> of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">{pagination.pages}</span>
                {" "}({pagination.total} total {filter === "offers" ? "offers" : "jobs"})
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.page === 1
                      ? "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                      : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage =
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1);

                    const showEllipsis =
                      (pageNum === 2 && pagination.page > 3) ||
                      (pageNum === pagination.pages - 1 && pagination.page < pagination.pages - 2);

                    if (showEllipsis) {
                      return (
                        <span
                          key={pageNum}
                          className="px-2 text-gray-400 dark:text-gray-600"
                        >
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pagination.page === pageNum
                            ? "bg-[#EFA938] text-white"
                            : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.page === pagination.pages
                      ? "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                      : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                  }`}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyJobs;
